import { Component, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Constants, FullPageMaskService, ILookup, SecurityService, SpinnerModule } from 'common';
import { combineLatest, debounceTime, distinctUntilChanged, filter, lastValueFrom, map, Observable, pairwise, startWith, Subscription, switchMap } from 'rxjs';
import { OrganisationDataFormService } from '../../services/organisation-data-form.service';
import { FetchingState, RequestState } from '../../interfaces/request.interface';
import { ICroCodeResult } from '../../interfaces/cro-result.interface';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { eircodeValidator } from '../../validators/eircode.validator';
import { insertIf } from '../../utilities/array/insertIf';
import { RegisterFormEvent, RegisterFormGroup } from '../../interfaces/forms/register-form.interface';
import { toCompanyDetailsForm } from '../signup/signup.utilities';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatButton } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { RegistrationService } from '../../services/registration.service';
import { downloadUrl } from '../../utilities/url/download.utilities';
import { AccountType } from '../../interfaces/account-type.interface';
import { erauReferenceValidator } from '../../validators/erau-reference.validator';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { RegistrationApiService } from '../../services/registration-api.service';
import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { RtpOpaAssignment } from '../../interfaces/rtp-opa-assignment.interface';

const allowedFileExtensions = ['pdf', 'zip'];
@Component({
  selector: 'si-registration-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatLabel,
    MatFormField,
    MatInput,
    MatRadioButton,
    MatRadioGroup,
    MatError,
    MatCheckbox,
    MatSelect,
    MatOption,
    MatButton,
    MatTooltip,
    MatIcon,
    MatTableModule,
    CommonModule,
    SpinnerModule,
  ],
  templateUrl: './registration-form.component.html',
  styleUrl: './registration-form.component.scss',
})
export class RegistrationFormComponent implements OnInit, OnDestroy {
  @Input() orgTypes: ILookup[];
  @Input() countries: ILookup[];
  @Input() accountTypes: ILookup[] = [];
  @Input() showSubmitButton = true;
  @Input() submitButtonText = 'Sign Up';
  @Output() formSubmitted = new EventEmitter<RegisterFormEvent>();

  maskService = inject(FullPageMaskService);
  organisationDataFormService = inject(OrganisationDataFormService);
  registrationService = inject(RegistrationService);
  securityService = inject(SecurityService);
  adminRegistrationApiService = inject(RegistrationApiService);

  private readonly subscription = new Subscription();

  public isSido: boolean = true;
  public isCroFetching = false;
  public isRegisteredEircodeDisabled = false;
  public isBusinessEircodeDisabled = false;

  public fileError = false;
  public fileMessage = '';
  public croPlaceholder: string;

  @ViewChild('fileUploadInput') filenameInput: ElementRef;

  RTPColumns = ['accountCode', 'organisationName'];

  get RTPDataSource() {
    return new MatTableDataSource<RtpOpaAssignment>(this.organisationDataFormService.RTPOPAList);
  }

  get hideIsCroOptionalSection() {
    return this.organisationDataFormService.hideIsCroOptionalSection;
  }

  get showPADownloadButton() {
    return this.organisationDataFormService.showPADownloadButton;
  }

  get showRTPOPAList() {
    return this.organisationDataFormService.showRTPOPAList;
  }

  get accountTypeDisabled() {
    return this.organisationDataFormService.formSignup.get('accountType').disabled;
  }

  get accountTypeValue() {
    return this.accountTypes.find(type => +type.value === +this.formSignup.get('accountType').value).key;
  }

  public ngOnInit(): void {
    this.croPlaceholder = this.hideIsCroOptionalSection ? 'CRO/Registration Number' : 'CRO Number';
    this.addSubscriptions();
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.resetFileDetails();
  }

  private addSubscriptions(): void {
    if (!this.securityService.isLoggedIn()) {
      this.addCroToggleSubscription();
    }
    this.addEircodeSubscription('businessAddressCountry', 'businessAddressEircode');
    this.addEircodeSubscription('registeredAddressCountry', 'registeredAddressEircode');
    this.addRequiredNumbersSubscription();
    this.addAddressSubscriptions();
    this.addErauSubscription();
  }

  private addAddressSubscriptions() {
    const registeredPrefix = 'registered';
    const businessPrefix = 'business';
    const addressFields = ['AddressLine1', 'AddressLine2', 'AddressLine3', 'AddressLine4', 'AddressCountry', 'AddressEircode'];

    this.subscription.add(
      combineLatest(addressFields.map((field) => this.getFormControl(`${registeredPrefix}${field}`).valueChanges.pipe(startWith(''))))
        .pipe(
          filter(() => this.getFormControl('useRegisteredAddressAsBusinessAddress').value),
          pairwise(),
          map(([previousValues, currentValues]: [string[], string[]]) => {
            const changedValueIndex = currentValues.findIndex((value: string, index) => previousValues[index] !== value);

            return { index: changedValueIndex, value: currentValues[changedValueIndex] };
          })
        )
        .subscribe(({ index, value }) => {
          const control = this.getFormControl(`${businessPrefix}${addressFields[index]}`);

          control.setValue(value, { emitEvent: false });
          control.updateValueAndValidity({ emitEvent: false });
        })
    );

    this.subscription.add(
      this.getFormControl('useRegisteredAddressAsBusinessAddress').valueChanges.subscribe((isSelected: boolean) => {
        addressFields.forEach((field) => {
          const businessControl = this.getFormControl(`${businessPrefix}${field}`);
          const registeredControl = this.getFormControl(`${registeredPrefix}${field}`);
          if (isSelected) {
            businessControl.disable({ emitEvent: false });
            businessControl.setValue(registeredControl.value, { emitEvent: false });
          } else {
            businessControl.enable({ emitEvent: false });
          }

          businessControl.updateValueAndValidity({ emitEvent: false });
        });
      })
    );
  }

  private addErauSubscription() {
    const accountTypeControl = this.getFormControl('accountType');

    this.subscription.add(
      accountTypeControl.valueChanges
        .pipe(
          debounceTime(600),
          distinctUntilChanged(),
        )
        .subscribe((value) => {
          const erauReferenceControl = this.getFormControl('erauReference');
          if (value === AccountType.PaOrMsp) {
            erauReferenceControl.addValidators([Validators.required, erauReferenceValidator()]);
          } else {
            erauReferenceControl.clearValidators();
          }

          erauReferenceControl.updateValueAndValidity();
        })
    )
  }

 

  private addCroToggleSubscription() {
    if (this.hideIsCroOptionalSection) {
      return;
    }
    this.subscription.add(
      this.getFormControl('isCroOptional').valueChanges.subscribe((value) => {
        const croControl = this.getFormControl('croNumber');
        const orgRegistrationNumberControl = this.getFormControl('orgRegistrationNumber');
        const fileControl = this.getFormControl('fileName');
        const optionalFields = ['taxRegistrationNumber', 'trademarkNumbers', 'orgRegistrationNumber'];
        const shoulduUpdateValidity = optionalFields.every((name) => !this.getFormControl(name).value);
        if (value) {
          fileControl.addValidators([Validators.required]);
          croControl.clearValidators();
        } else {
          fileControl.clearValidators();
          croControl.addValidators([Validators.required, Validators.maxLength(6), Validators.pattern("^[0-9]*$")]);
          this.resetFileDetails();
        }

        if (shoulduUpdateValidity) {
          optionalFields.forEach((name) => {
            const control = this.getFormControl(name);
            control.setValidators([
              ...insertIf(value, Validators.required),
              Validators.maxLength(20),
            ]);
            control.updateValueAndValidity();
          });
        }

        croControl.setValue('');
        orgRegistrationNumberControl.setValue('');
        croControl.updateValueAndValidity();
        fileControl.updateValueAndValidity();
      })
    );
  }

  private addEircodeSubscription(countryField: string, eircodeField: string): void {
    this.subscription.add(
      this.getFormControl(countryField).valueChanges.subscribe((value) => {
        const eirCodeControl = this.getFormControl(eircodeField);
        const isRegisteredEircode = eircodeField === 'registeredAddressEircode';
        const isBusinessEircode = eircodeField === 'businessAddressEircode';
        const shouldStayDisabled = (isRegisteredEircode && this.isRegisteredEircodeDisabled) || 
                                   (isBusinessEircode && this.isBusinessEircodeDisabled);

        if (value) {
          if (!shouldStayDisabled) {
            eirCodeControl.enable();
          }
          eirCodeControl.setValidators([
            Validators.required,
            Validators.maxLength(20),
            ...insertIf(value === Constants.irelandCountryString, eircodeValidator()),
          ]);
        } else {
          eirCodeControl.disable();
          eirCodeControl.setValue('');
        }

        eirCodeControl.updateValueAndValidity();
      })
    );
  }

  private addRequiredNumbersSubscription(): void {
    const fields = ['taxRegistrationNumber', 'trademarkNumbers', 'orgRegistrationNumber'];

    this.subscription.add(
      combineLatest(fields.map((field) => this.getFormControl(field).valueChanges.pipe(startWith(''))))
        .pipe(
          filter(() => this.getFormControl('isCroOptional')?.value),
          pairwise(),
          filter(([previousValues, currentValues]) => {
            return (
              (previousValues.every((value) => !value) && currentValues.some((value) => value)) ||
              (previousValues.some((value) => value) && currentValues.every((value) => !value))
            );
          }),
          map(([, currentValues]) => currentValues.some((value) => value))
        )
        .subscribe((isValid: boolean) => {
          fields.forEach((field) => {
            const control = this.getFormControl(field);
            if (isValid) {
              control.removeValidators(Validators.required);
            } else {
              control.addValidators(Validators.required);
            }
            control.updateValueAndValidity();
          });
        })
    );
  }

  public get formSignup(): RegisterFormGroup {
    return this.organisationDataFormService.formSignup;
  }

  public getFormControl<T>(fieldName: string): FormControl {
    return this.formSignup.get(fieldName) as FormControl<T>;
  }

  public get isCroOptional() {
    return this.formSignup.get('isCroOptional')?.value;
  }

  public get useRegisteredAddressAsBusinessAddress() {
    return this.formSignup.get('useRegisteredAddressAsBusinessAddress').value;
  }

  public get isPaAccountType() {
    return this.formSignup.get('accountType').value === AccountType.PaOrMsp;
  }

  public get includeSingleContactEntry() {
    return (
      !!this.formSignup.get('contactFirstName') &&
      !!this.formSignup.get('contactLastName') &&
      !!this.formSignup.get('contactPhoneNumber') &&
      !!this.formSignup.get('contactEmailAddress')
    );
  }

  public get allowedExtensions() {
    return allowedFileExtensions.map((extension) => `.${extension}`);
  }

  public async handleDownloadButtonClick() {
    const { blob, name } = await lastValueFrom(this.registrationService.getPublicFile$());
    downloadUrl(blob, name);
  }

  private resetFileDetails() {
    const fileNameControl = this.getFormControl('fileName');
    fileNameControl.setValue('');
    fileNameControl.clearValidators();
    this.fileError = false;
    this.fileMessage = '';
    this.organisationDataFormService.file = null;
  }

  public handleSubmit() {
    const event = this.organisationDataFormService.toRegisterFormEvent();
    this.formSubmitted.emit(event);
  }

  

  onFileChange(files: FileList) {
    this.fileError = false;

    if (files.length === 0) {
      this.getFormControl('fileName').setValue('');
      this.getFormControl('fileContentType').setValue('');
      this.getFormControl('fileContent').setValue('');
      this.fileMessage = 'no file selected';
      this.fileError = true;
    } else {
      this.organisationDataFormService.file = files.item(0);
      this.fileMessage = files.item(0).name;
      const fileExtension = files.item(0).name.split('.').pop().toLocaleLowerCase();

      if (!allowedFileExtensions.includes(fileExtension)) {
        this.fileError = true;
        this.getFormControl('fileName').setValue('');
        this.fileMessage =
          'Invalid file type. The following type(s) are allowed: ' +
          allowedFileExtensions.reduce((accum, curr) => {
            return accum + curr + ',';
          }, '');
      } else {
        this.getFormControl('fileName').setValue(files.item(0).name);
      }
    }
  }

  async handleDownloadAPIButtonClick(type: 'registeredPA' | 'registeredSenderID' | 'senderIDOPAAuthorised', format: 'csv' | 'json') {
    this.maskService.showLoading();
    try {
      const accountId = this.getFormControl('accountID').value;
      let value: Observable<Blob> = null;
      let filename: string = '';
      switch (type) {
        case 'registeredPA':
          value = this.adminRegistrationApiService.getRegisteredPAsFile$(accountId, format);
          filename = 'Registered PA List';
          break;
        case 'registeredSenderID':
          value = this.adminRegistrationApiService.getRegisteredSenderIDsFile$(accountId, format);
          filename = 'Registered SenderID List';
          break;
        case 'senderIDOPAAuthorised':
          value = this.adminRegistrationApiService.getOpaAuthorisedSenderIdsFile$(accountId, format);
          filename = `Sender ID OPA Authorised List for SIDR-${accountId.toString().padStart(5, '0')}`;
          break;
        default:
          break;
      }
      const file = await lastValueFrom(value);
      const date = this.getTimestamp();
      downloadUrl(file, `${filename}__${date}.${format}`);
    } finally {
      this.maskService.hideLoading();
    }
  }

  getTimestamp() {
    const now = new Date();
    const pad = (n) => n.toString().padStart(2, '0');
    const day = pad(now.getDate());
    const month = pad(now.getMonth() + 1);
    const year = now.getFullYear();
    const hours = pad(now.getHours());
    const minutes = pad(now.getMinutes());
    const seconds = pad(now.getSeconds());
    const formattedDate = `${day}_${month}_${year}__${hours}_${minutes}_${seconds}`;
    return formattedDate;
  }
}
